import React, { useState, useCallback } from 'react';
import { projects } from '../../data/projects';
import SpotichouGuide from './SpotichouGuide';
import styles from './Projects.module.css';

type MediaItem =
  | { type: 'video'; src: string; poster?: string }
  | { type: 'image'; src: string };

const Projects: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const project = projects[activeTab];
  const screenshots = project.screenshots ?? [];
  const videos = project.videos ?? (project.video ? [project.video] : []);
  const videoPosters = project.videoPosters ?? [];
  const poster = videoPosters[0] ?? screenshots[0];
  const layout = project.mediaLayout ?? 'carousel';

  const mediaItems: MediaItem[] = [
    ...videos.map((src, i) => ({ type: 'video' as const, src, poster: videoPosters[i] })),
    ...screenshots.map(src => ({ type: 'image' as const, src })),
  ];
  const hasMedia = mediaItems.length > 0;

  const handleTabChange = useCallback((index: number) => {
    setActiveTab(index);
    setCarouselIndex(0);
  }, []);

  const prev = () => setCarouselIndex(i => (i - 1 + mediaItems.length) % mediaItems.length);
  const next = () => setCarouselIndex(i => (i + 1) % mediaItems.length);

  return (
    <section className={styles.projects}>
      <div className="container">
        <div className={styles.header}>
          <h2 className={styles.sectionTitle}>Projets personnels</h2>
        </div>

        <div className={styles.tabBar}>
          {projects.map((p, index) => (
            <button
              key={p.id}
              className={`${styles.tab} ${index === activeTab ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(index)}
            >
              {p.title}
              {p.titleTag && (
                <span className={styles.titleTag}>{p.titleTag}</span>
              )}
            </button>
          ))}
        </div>

        <div className={styles.projectCard}>
          <div className={styles.projectHeader}>
            <h3 className={styles.projectTitle}>{project.title}</h3>
            <p className={styles.projectSubtitle}>{project.subtitle}</p>

            {project.highlights && project.highlights.length > 0 && (
              <div className={styles.highlights}>
                {project.highlights.map((h, index) => (
                  <span key={index} className={styles.highlight}>
                    ✦ {h}
                  </span>
                ))}
              </div>
            )}

            <div className={styles.tags}>
              {project.tags.map((tag, index) => (
                <span key={index} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {project.id === 'spotichou' && <SpotichouGuide />}

          <div className={styles.section}>
            <h4 className={styles.sectionHeading}>Problème</h4>
            <div className={styles.sectionContent}>
              <p>{project.problem}</p>
            </div>
          </div>

          <div className={styles.section}>
            <h4 className={styles.sectionHeading}>Contexte</h4>
            <div className={styles.sectionContent}>
              <p>{project.context}</p>
            </div>
          </div>

          <div className={styles.section}>
            <h4 className={styles.sectionHeading}>Décisions clés</h4>
            <div className={styles.decisionsGrid}>
              <div className={styles.decisionColumn}>
                <h4>Produit</h4>
                <ul>
                  {project.decisions.product.map((decision, index) => (
                    <li key={index}>{decision}</li>
                  ))}
                </ul>
              </div>
              <div className={styles.decisionColumn}>
                <h4>Technique</h4>
                <ul>
                  {project.decisions.technical.map((decision, index) => (
                    <li key={index}>{decision}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {project.impact.length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionHeading}>Impact</h4>
              <ul className={styles.impactList}>
                {project.impact.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <div className={styles.section}>
            <h4 className={styles.sectionHeading}>Apprentissages</h4>
            <ul className={styles.impactList}>
              {project.learnings.map((learning, index) => (
                <li key={index}>{learning}</li>
              ))}
            </ul>
          </div>

          {hasMedia && (
            <div className={styles.section}>
              <h4 className={styles.sectionHeading}>Aperçu</h4>

              {layout === 'gallery' ? (
                <div
                  className={styles.gallery}
                  style={{ ['--tile-aspect' as string]: project.mediaAspect ?? '3 / 2' }}
                >
                  {mediaItems.map((item, i) => (
                    <button
                      key={i}
                      className={styles.galleryTile}
                      onClick={() => setLightbox(item)}
                      aria-label={
                        item.type === 'video'
                          ? 'Lire la vidéo'
                          : `Agrandir la capture ${i + 1}`
                      }
                    >
                      {item.type === 'video' ? (
                        <>
                          {(item.poster ?? poster) && (
                            <img src={item.poster ?? poster} alt="" loading="lazy" />
                          )}
                          <span className={styles.galleryPlay} aria-hidden="true">▶</span>
                        </>
                      ) : (
                        <img src={item.src} alt={`Aperçu ${i + 1}`} loading="lazy" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className={styles.carousel}>
                  <div className={styles.carouselMain}>
                    {mediaItems[carouselIndex].type === 'video' ? (
                      <video
                        className={styles.carouselVideo}
                        controls
                        preload="metadata"
                        poster={poster}
                        key={mediaItems[carouselIndex].src}
                      >
                        <source src={mediaItems[carouselIndex].src} type="video/mp4" />
                        <source src={mediaItems[carouselIndex].src} type="video/quicktime" />
                      </video>
                    ) : (
                      <button
                        className={styles.carouselImageBtn}
                        onClick={() => setLightbox(mediaItems[carouselIndex])}
                        aria-label="Agrandir l'image"
                      >
                        <img src={mediaItems[carouselIndex].src} alt={`Aperçu ${carouselIndex}`} />
                      </button>
                    )}

                    {mediaItems.length > 1 && (
                      <>
                        <button className={`${styles.carouselArrow} ${styles.carouselArrowLeft}`} onClick={prev} aria-label="Précédent">‹</button>
                        <button className={`${styles.carouselArrow} ${styles.carouselArrowRight}`} onClick={next} aria-label="Suivant">›</button>
                      </>
                    )}
                  </div>

                  {mediaItems.length > 1 && (
                    <div className={styles.carouselThumbs}>
                      {mediaItems.map((item, i) => (
                        <button
                          key={i}
                          className={`${styles.carouselThumb} ${i === carouselIndex ? styles.carouselThumbActive : ''}`}
                          onClick={() => setCarouselIndex(i)}
                          aria-label={item.type === 'video' ? 'Vidéo' : `Capture ${i}`}
                        >
                          {item.type === 'video' ? (
                            <span className={styles.carouselThumbVideo}>▶</span>
                          ) : (
                            <img src={item.src} alt="" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {project.url && (
            <div className={styles.projectLink}>
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.projectButton}
              >
                Voir le projet →
              </a>
            </div>
          )}
        </div>
      </div>

      {lightbox && (
        <div
          className={styles.lightboxOverlay}
          onClick={() => setLightbox(null)}
        >
          <button
            className={styles.lightboxClose}
            onClick={() => setLightbox(null)}
            aria-label="Fermer"
          >
            ✕
          </button>

          {lightbox.type === 'video' ? (
            <video
              className={styles.lightboxMedia}
              src={lightbox.src}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={lightbox.src}
              alt="Capture agrandie"
              className={styles.lightboxMedia}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default Projects;
